/**
 * @author Muhammad Omran
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-12-03
 * @description Implementation of my saved items page.
 * @filename my-saved-items.tsx
 */
import {
  GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY,
  GRAPHQL_TYPE_PODCAST_STORY,
  GRAPHQL_TYPE_POSTER,
  PreviewKind,
  GRAPHQL_TYPE_PITCH_VIDEO_STORY,
} from "@radivision/graphql";
import React from "react";
import { CarouselItem } from "../../../component-configuration/carousel-item";
import { Show } from "../../../component-configuration/show";
import { REACT_USER_CONTEXT } from "../../../contexts/react-user-context";
import { getCarouselItem } from "../../../utilities/general";
import { RdvCarousel } from "../../lists/rdv-carousel";
import { FiltersWidget } from "../../user-widgets/filters-widget";

/**
 * The props of the MySavedItems component.
 *
 * @interface
 */
interface MySavedItemsProps {}

/**
 * interface that defines the state of the component
 *
 * @interface
 */
interface MySavedItemsState {
  /**
   * current selected tab
   *
   * @type {"Videos" | "Resources"}
   * @memberof MySavedItemsState
   */
  currentTab: "Videos" | "Resources";

  /**
   * user saved items
   *
   * @type {CarouselItem[]}
   * @memberof MySavedItemsState
   */
  savedItems: CarouselItem[];
}

/**
 * A React component that renders the MySavedItems component
 *
 * @extends {React.Component}
 */
export class MySavedItems extends React.Component<
  MySavedItemsProps,
  MySavedItemsState
> {
  /**
   * Radivision app user Context
   */
  static contextType = REACT_USER_CONTEXT;

  /**
   * Constructor.
   *
   * @param {MySavedItems} props The props of the component.
   */
  constructor(props: MySavedItemsProps) {
    super(props);

    this.state = {
      currentTab: "Videos",
      savedItems: null,
    };

    // ... binding
    this.getUserSavedItemsList = this.getUserSavedItemsList.bind(this);
  }

  /**
   * Invoked when the component finishs rendering for the very first time
   */
  componentDidMount() {
    this.getUserSavedItemsList();
  }

  /**
   * to retrieve user saved items list from the context
   *
   * @memberof MySavedItems
   */
  getUserSavedItemsList() {
    const { user, userSavedItems } = this.context;
    if (
      user &&
      user.myList &&
      userSavedItems &&
      userSavedItems.items &&
      userSavedItems.items.edges &&
      userSavedItems.items.edges.length
    ) {
      const items: CarouselItem[] = [];
      for (const edge of userSavedItems.items.edges) {
        if (edge !== undefined && edge !== null) {
          const item = edge.listItem.item;

          items.push(
            getCarouselItem(
              edge,
              userSavedItems,
              0,
              {
                containerWidthRatio: 1.5,
                numberOfItems: 5,
              },
              item.__typename === GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY ||
                item.__typename === GRAPHQL_TYPE_PODCAST_STORY ||
                item.__typename === GRAPHQL_TYPE_POSTER ||
                (item.link && item.link.includes("youtu"))
                ? PreviewKind.CAROUSEL
                : PreviewKind.SQUARE,
              true
            )
          );
        }
      }

      this.setState({ savedItems: items });
    }
  }

  /**
   * invoked when component finished updating
   */
  componentDidUpdate() {
    const { userSavedItems } = this.context;
    if (
      (userSavedItems && !this.state.savedItems) ||
      (this.state.savedItems && !userSavedItems)
    ) {
      this.getUserSavedItemsList();
    }
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered list.
   */
  render(): React.ReactNode {
    const { userSavedItems } = this.context;
    const videoItems: CarouselItem[] = this.state.savedItems
      ? this.state.savedItems.filter(
          (item: CarouselItem) =>
            item.type === GRAPHQL_TYPE_PODCAST_STORY ||
            item.type === GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY ||
            item.type === GRAPHQL_TYPE_PITCH_VIDEO_STORY ||
            item.type === GRAPHQL_TYPE_POSTER ||
            (item.link && item.link.includes("youtu"))
        )
      : [];
    const resourceItems: CarouselItem[] = this.state.savedItems
      ? this.state.savedItems.filter(
          (item: CarouselItem) =>
            item.type !== GRAPHQL_TYPE_PODCAST_STORY &&
            item.type !== GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY &&
            item.type !== GRAPHQL_TYPE_PITCH_VIDEO_STORY &&
            item.type !== GRAPHQL_TYPE_POSTER &&
            (!item.link || !item.link.includes("youtu"))
        )
      : [];

    const VideoCarousel: Show = userSavedItems
      ? {
          id: userSavedItems.id,
          title: "My Saved Videos",
          description: "",
          revision: userSavedItems.revision,
          items: videoItems,
        }
      : null;

    const ResourceCarousel: Show = userSavedItems
      ? {
          id: userSavedItems.id,
          title: "My Saved Resources",
          description: "",
          revision: userSavedItems.revision,
          items: resourceItems,
        }
      : null;
    return (
      <div className="my-saved-items">
        <div className="title">
          <h3>Saved</h3>
        </div>
        <FiltersWidget
          json={{
            filters: [
              {
                id: "0",
                label: "Videos",
              },
              {
                id: "1",
                label: "Resources",
              },
            ],
          }}
          alignItems="left"
          resetTopMargin={true}
          hideAll={true}
          disableStickey={true}
          customFilterHandler={(label: string) => {
            const newTab: "Videos" | "Resources" =
              label === "Resources" ? "Resources" : "Videos";
            this.setState({ currentTab: newTab });
          }}
        />
        {this.state.currentTab === "Videos" ? (
          <div className="section-header">
            <div>
              {// validate provided props before attempt to assign gridContent
              videoItems.length === 0 ? (
                <h4 className="no-content-to-show">No saved Videos to show</h4>
              ) : (
                <RdvCarousel
                  key={`show-${userSavedItems.id}`}
                  displayMode="GridView"
                  show={VideoCarousel}
                  list={this.context?.userSavedItems}
                  itemPreviewType={PreviewKind.CAROUSEL}
                  hasDetails={true}
                  hideTitle={true}
                  numberOfItems={5}
                  pageInfo={userSavedItems.items.pageInfo}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="section-header">
            <div>
              {// validate provided props before attempt to assign gridContent
              resourceItems.length === 0 ? (
                <h4 className="no-content-to-show">
                  No saved Resources to show
                </h4>
              ) : (
                <RdvCarousel
                  key={`show-${userSavedItems.id}`}
                  displayMode="GridView"
                  show={ResourceCarousel}
                  itemPreviewType={PreviewKind.SQUARE}
                  hasDetails={true}
                  hideTitle={true}
                  numberOfItems={7}
                  pageInfo={userSavedItems.items.pageInfo}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
